import { canInAnyScope } from '@testflow/shared';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { useScopedUser } from '../../lib/project-roles.js';
import { useCurrentProject } from '../../lib/use-project.js';
import { DigestPanel } from './panels/digest-panel.js';
import { ExitCriteriaPanel } from './panels/exit-criteria-panel.js';
import { GamificationPanel } from './panels/gamification-panel.js';
import { SignoffTermPanel } from './panels/signoff-term-panel.js';
import { SourceRefPanel } from './panels/source-ref-panel.js';
import { StructurePanel } from './panels/structure-panel.js';
import { UsersPanel } from './panels/users-panel.js';
import { VocabPanel } from './panels/vocab-panel.js';

// Design-Spec 3.7: Admin/Projektkonfiguration. F2 baute nur die Shell — Sidebar (Projekt-Sektion +
// System-Sektion) und einen Panel-Bereich, der je Auswahl per lokalem useState umschaltet. F3
// ersetzte die vocab-Placeholder durch die echten Editoren (VocabPanel für DEF-3-Severity/
// Fehlertyp-Vokabular, ExitCriteriaPanel für REP-5-Exit-Kriterien — Letzteres hat im Design-Spec
// keinen eigenen Sidebar-Eintrag, bekam hier deshalb einen neuen). F4 (diese Task) ersetzt die
// letzten drei Placeholder: SignoffTermPanel (SGN-1-Begriffskonfiguration), DigestPanel
// (NOT-5-Admin), GamificationPanel (GAM-1-Schalter). Task 9 (#30) ergänzt UsersPanel
// (Nutzer-/Rollenverwaltung je Projekt) — Sidebar-Eintrag nur sichtbar, wenn der Nutzer
// `user.manage` darf.
type ProjectPanelKey =
  | 'general'
  | 'structure'
  | 'users'
  | 'vocab'
  | 'exit-criteria'
  | 'terms'
  | 'digest'
  | 'gamification'
  // #112 (A6): Schalter für die Quell-ID im Katalog und in der Testplan-Auswahl.
  | 'source-ref';

const PROJECT_ITEMS: { key: ProjectPanelKey; labelKey: string }[] = [
  { key: 'general', labelKey: 'admin.general' },
  { key: 'structure', labelKey: 'admin.structure.navLabel' },
  { key: 'users', labelKey: 'admin.users.navLabel' },
  { key: 'vocab', labelKey: 'admin.vocab.navLabel' },
  { key: 'exit-criteria', labelKey: 'admin.exit.navLabel' },
  { key: 'terms', labelKey: 'admin.terms' },
  { key: 'digest', labelKey: 'admin.digest.navLabel' },
  { key: 'gamification', labelKey: 'admin.gamification' },
  { key: 'source-ref', labelKey: 'admin.sourceRef.navLabel' },
];

function GeneralPanel() {
  const { t } = useTranslation();
  const { project, isLoading } = useCurrentProject();

  if (isLoading) {
    return <div className="p-6 text-text-muted">{t('common.loading')}</div>;
  }
  if (!project) {
    return <div className="p-6 text-text-muted">{t('catalog.noProject')}</div>;
  }
  return (
    <div className="space-y-4 p-6">
      <h2 className="text-lg font-bold text-text-primary">{t('admin.general')}</h2>
      <section className="rounded-card border border-[var(--border)] bg-bg-surface p-4">
        <dl className="text-sm text-text-body">
          <dt className="text-text-muted">{t('admin.projectName')}</dt>
          <dd className="font-medium text-text-primary">{project.name}</dd>
        </dl>
      </section>
    </div>
  );
}

function ProjectPanel({ panel }: { panel: ProjectPanelKey }) {
  switch (panel) {
    case 'general':
      return <GeneralPanel />;
    case 'structure':
      return <StructurePanel />;
    case 'users':
      return <UsersPanel />;
    case 'vocab':
      return <VocabPanel />;
    case 'exit-criteria':
      return <ExitCriteriaPanel />;
    case 'terms':
      return <SignoffTermPanel />;
    case 'digest':
      return <DigestPanel />;
    case 'gamification':
      return <GamificationPanel />;
    case 'source-ref':
      return <SourceRefPanel />;
    default: {
      const _exhaustive: never = panel;
      void _exhaustive;
      return null;
    }
  }
}

function sidebarItemClass(active: boolean): string {
  return `rounded-chip px-3 py-2 text-left text-sm ${
    active ? 'bg-brand-accent/10 text-brand-accent' : 'text-text-body hover:bg-bg-page'
  }`;
}

export function AdminPage() {
  const { t } = useTranslation();
  // #23: Rollen DIESES Projekts (lib/project-roles.ts) — die Admin-Sicht darf nicht aufgehen,
  // weil der Nutzer irgendwo anders Testmanager ist.
  const { user } = useScopedUser();
  const [activePanel, setActivePanel] = useState<ProjectPanelKey>('general');

  const roles = user?.roles.map((assignment) => assignment.role) ?? [];
  const isAdmin = roles.includes('admin');
  const authorized = isAdmin || (user ? canInAnyScope(user, 'project.configure') : false);
  const canManageUsers = user ? canInAnyScope(user, 'user.manage') : false;
  const projectItems = PROJECT_ITEMS.filter((i) => i.key !== 'users' || canManageUsers);

  if (!authorized) {
    return <div className="p-6 text-text-muted">{t('errors.forbidden')}</div>;
  }

  return (
    <div className="flex h-[calc(100vh-58px)]">
      <aside className="w-64 shrink-0 space-y-6 border-r border-[var(--border)] bg-bg-surface p-4">
        <h1 className="text-sm font-bold text-text-primary">{t('admin.title')}</h1>
        <div className="space-y-1">
          <h2 className="px-3 text-xs font-semibold uppercase text-text-muted">
            {t('admin.project')}
          </h2>
          <nav className="flex flex-col gap-1">
            {projectItems.map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => setActivePanel(item.key)}
                className={sidebarItemClass(activePanel === item.key)}
              >
                {t(item.labelKey)}
              </button>
            ))}
          </nav>
        </div>
        {isAdmin && (
          <div className="space-y-1">
            <h2 className="px-3 text-xs font-semibold uppercase text-text-muted">
              {t('admin.system')}
            </h2>
            <nav className="flex flex-col gap-1">
              <NavLink to="/admin/import" className={({ isActive }) => sidebarItemClass(isActive)}>
                {t('admin.import')}
              </NavLink>
            </nav>
          </div>
        )}
      </aside>
      <main className="flex-1 overflow-y-auto">
        <ProjectPanel panel={activePanel} />
      </main>
    </div>
  );
}
